// Dashboard controls use authored options or source facets carried in chart results.
// Dates and budget bounds can target a dataset’s input cohort before aggregation.
import { useEffect, useState } from "react";

import { useQuery } from "@tanstack/react-query";

import { Button, Input, MultiSelect, Popover } from "@bi/shared";
import type { ChartSpec, DatasetSpec, FilterState, NativeFilterSpec } from "@bi/shared/types";

import {
  clearFilterValue,
  clearParameterOverrides,
  filterValueForSelection,
  getFilterValue,
  rangeBounds,
  resolveFilterScope,
  setFilterValue,
  setParameterOverrides,
  setTimeRange,
  withImplicitTimeFilter,
} from "../lib/filter-state";
import { type CalDate, withDatePart } from "../lib/calendar";
import {
  TIME_RANGE_PRESETS,
  type BoundPrecision,
  boundPrecision,
  convertInputPrecision,
  describeTimeRange,
  inputValueToWireBound,
  isValidCustomRange,
  matchingPresetLabel,
  presetRange,
  wireBoundToInputValue,
} from "../lib/time-range";
import { RangeCalendar } from "./range-calendar";
import type { ChartRenderState } from "../lib/bi-query";
import { resultFilterOptions } from "../lib/filter-options";
import { biSourceValuesQuery } from "../lib/queries";

export interface FilterBarProps {
  chartStates?: Readonly<Record<string, ChartRenderState>>;
  filters: readonly NativeFilterSpec[];
  charts: Record<string, ChartSpec>;
  datasets: Record<string, DatasetSpec>;
  state: FilterState;
  onChange: (next: FilterState) => void;
}

/** Every filter that resolves to inactive on every chart on the dashboard —
 *  not hidden (design doc §4: a filter that carries no equivalent chart
 *  right now stays visible), just rendered muted. */
function isFilterInactiveEverywhere(
  filter: NativeFilterSpec,
  charts: Record<string, ChartSpec>,
  datasets: Record<string, DatasetSpec>,
): boolean {
  const scope = resolveFilterScope(filter, charts, datasets);
  return scope.length > 0 && scope.every((s) => !s.active);
}

export function FilterBar({
  filters,
  charts,
  datasets,
  state,
  onChange,
  chartStates = {},
}: FilterBarProps) {
  const resolved = withImplicitTimeFilter(filters).map((filter) => {
    if (!filter.options_result_column) return filter;
    const result = Object.entries(chartStates).find(
      ([id, value]) =>
        charts[id]?.dataset_uuid === filter.target_dataset_uuid &&
        value.status === "ready" &&
        value.result.columns.includes(filter.options_result_column!),
    )?.[1];
    const value = getFilterValue(state, filter.target_column ?? "")?.value;
    const selected = Array.isArray(value)
      ? value.filter((v): v is string => typeof v === "string")
      : [];
    return {
      ...filter,
      options: resultFilterOptions(
        filter,
        result?.status === "ready" ? result.result : undefined,
        selected,
      ),
    };
  });
  return (
    <div className="bi-filter-bar">
      {resolved.map((filter) => (
        <FilterControl
          key={filter.id}
          filter={filter}
          datasets={datasets}
          state={state}
          onChange={onChange}
          inactive={isFilterInactiveEverywhere(filter, charts, datasets)}
        />
      ))}
    </div>
  );
}

type FilterControlProps = {
  filter: NativeFilterSpec;
  datasets: Record<string, DatasetSpec>;
  state: FilterState;
  onChange: (next: FilterState) => void;
  inactive: boolean;
};

/** Dispatches by `filter.kind`. No hooks of its own, so each branch's
 *  component can use hooks unconditionally.
 *
 *  A `select` filter forks on where its vocabulary comes from: `options_from`
 *  (live source values), authored `options` or `options_result_column`
 *  (buttons), else the free-text box. */
function FilterControl(props: FilterControlProps) {
  if (props.filter.kind === "time_range") return <TimeRangeFilterControl {...props} />;
  if (props.filter.kind === "range") return <RangeFilterControl {...props} />;
  // `options_from` FIRST: a control that names a live source is answered by the
  // source, not by whatever authored `options` happen to sit beside it.
  if (props.filter.options_from) return <SourceValuesFilterControl {...props} />;
  if (props.filter.options.length > 0 || props.filter.options_result_column)
    return <OptionsFilterControl {...props} />;
  return <SelectFilterControl {...props} />;
}

/**
 * A `select` filter whose vocabulary comes from the WAREHOUSE
 * (`options_from: {source_id, column}`), resolved through
 * `GET /api/bi/sources/{id}/values/{column}`.
 *
 * Without it a `<MultiSelect>` could only be fed a fabricated list, so an
 * open-vocabulary filter would fall back to a free-text box. `options_from`
 * wins over authored `options` (see `FilterControl`).
 *
 * Three states, and the middle one is the point of the endpoint's `status`
 * field: `ready` shows the options; `pending` means NOTHING WAS CACHED and a
 * job was enqueued, so the control says it is loading rather than showing an
 * empty list that reads as "this column has no values"; `capped` means the list
 * is INCOMPLETE, so the free-text box comes back alongside it — a reader whose
 * value is past the cap needs a way to name it.
 */
function SourceValuesFilterControl(props: FilterControlProps) {
  const from = props.filter.options_from;
  const values = useQuery(biSourceValuesQuery(from?.source_id ?? "", from?.column ?? ""));
  const data = values.data;
  if (!data || data.status === "pending") {
    return (
      <div className="bi-filter bi-filter--inactive">
        <span className="bi-filter__label">{props.filter.name}</span>
        <span className="bi-filter__range-summary">
          {values.isError ? "Options unavailable" : "Loading options…"}
        </span>
      </div>
    );
  }
  const options = data.values.map((value) => ({ label: value, value }));
  if (data.capped) {
    // The list is short of the whole vocabulary, so the typed box is the only
    // way to reach a value the cap cut off. Both controls, not one: the options
    // that ARE known still save most readers the typing.
    return (
      <>
        <OptionsFilterControl {...props} filter={{ ...props.filter, options }} />
        <SelectFilterControl {...props} />
      </>
    );
  }
  return <OptionsFilterControl {...props} filter={{ ...props.filter, options }} />;
}

/**
 * A `select` filter over an AUTHORED closed vocabulary, rendered as a button
 * group — the same control shape as the time-range presets right beside it,
 * because it is the same kind of choice.
 *
 * This is what makes a partitioning filter usable at all. The lifecycle
 * funnel's `cohort_window` slices its dataset into four disjoint pre-computed
 * windows (`7d`/`30d`/`90d`/`all`), so picking one is not a refinement, it is
 * the difference between a funnel and four funnels stacked on top of each
 * other. Asking a reader to TYPE `30d`, exactly, into a text box to get that
 * is a control that mostly answers with an empty tile.
 *
 * Single-select by default and multi-select only where the author said so:
 * with `multi_select` false, clicking an option REPLACES the selection (and
 * the pressed one cannot be un-pressed into an empty state, which for a
 * partitioning column is never a view anyone wants). With it true, options
 * toggle and the applied value is every pressed one.
 */
function OptionsFilterControl({ filter, state, onChange, inactive }: FilterControlProps) {
  const column = filter.target_column ?? "";
  const current = getFilterValue(state, column);
  const selected = Array.isArray(current?.value) ? (current.value as string[]) : [];

  const apply = (next: string[]) => {
    const value = filterValueForSelection(filter, next);
    onChange(
      value
        ? setFilterValue(state, value.column, value.op, value.value)
        : clearFilterValue(state, column),
    );
  };

  const toggle = (optionValue: string) => {
    if (!filter.multi_select) {
      apply(selected.includes(optionValue) ? selected : [optionValue]);
      return;
    }
    apply(
      selected.includes(optionValue)
        ? selected.filter((v) => v !== optionValue)
        : [...selected, optionValue],
    );
  };

  return (
    <div
      className={inactive ? "bi-filter bi-filter--inactive" : "bi-filter"}
      title={inactive ? "Applies to no chart on this dashboard" : undefined}
    >
      <span className="bi-filter__label">{filter.name}</span>
      {filter.multi_select ? (
        <MultiSelect
          label={filter.name}
          allLabel={
            filter.options_result_column && !filter.options.length ? "Loading countries…" : "All"
          }
          options={filter.options}
          selected={selected}
          onChange={(next) => apply([...next])}
        />
      ) : (
        <div className="bi-filter__presets" role="group" aria-label={filter.name}>
          {filter.options.map((option) => {
            const pressed = selected.includes(option.value);
            return (
              <Button
                key={option.value}
                variant={pressed ? "solid" : "outline"}
                tone="neutral"
                size="sm"
                aria-pressed={pressed}
                onClick={() => toggle(option.value)}
              >
                {option.label}
              </Button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** Presets are the one-click common case. A custom range sits beside them,
 *  never behind a modal. The summary renders ONLY for a custom range: for a
 *  preset, `describeTimeRange` returns the preset's own name and would repeat
 *  the pressed button's label. */
function TimeRangeFilterControl({ filter, state, onChange, inactive }: FilterControlProps) {
  const activePreset = matchingPresetLabel(state.time_range_start, state.time_range_end);
  return (
    <div
      className={inactive ? "bi-filter bi-filter--inactive" : "bi-filter"}
      title={inactive ? "Applies to no chart on this dashboard" : undefined}
    >
      <span className="bi-filter__label">{filter.name}</span>
      <div className="bi-filter__presets">
        {TIME_RANGE_PRESETS.map((preset) => (
          <Button
            key={preset.label}
            variant={activePreset === preset.label ? "solid" : "outline"}
            tone="neutral"
            size="sm"
            onClick={() => {
              const range = presetRange(preset.days);
              onChange(setTimeRange(state, range.start, range.end));
            }}
          >
            {preset.label}
          </Button>
        ))}
        <CustomRangeControl state={state} onChange={onChange} active={activePreset === null} />
      </div>
      {activePreset === null ? (
        <span className="bi-filter__range-summary">
          {describeTimeRange(state.time_range_start, state.time_range_end)}
        </span>
      ) : null}
    </div>
  );
}

/** The custom-range popover: two inputs (a bare day by default, switched to
 *  a moment together by the "specific time" toggle — the UI can never
 *  produce a mixed-precision pair, matching the backend's own refusal of
 *  one) plus Apply/Clear. Its own local draft state is uncommitted until
 *  Apply, mirroring `SelectFilterControl`'s draft-then-commit pattern below,
 *  and resets from the live `FilterState` every time the popover opens so a
 *  cancelled edit never lingers into the next open. */
function CustomRangeControl({
  state,
  onChange,
  active,
}: {
  state: FilterState;
  onChange: (next: FilterState) => void;
  active: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [precision, setPrecision] = useState<BoundPrecision>(
    () => (state.time_range_start && boundPrecision(state.time_range_start)) || "date",
  );
  const [start, setStart] = useState(() =>
    wireBoundToInputValue(state.time_range_start, precision),
  );
  const [end, setEnd] = useState(() => wireBoundToInputValue(state.time_range_end, precision));

  const resetDraft = () => {
    const nextPrecision =
      (state.time_range_start && boundPrecision(state.time_range_start)) || "date";
    setPrecision(nextPrecision);
    setStart(wireBoundToInputValue(state.time_range_start, nextPrecision));
    setEnd(wireBoundToInputValue(state.time_range_end, nextPrecision));
  };

  const startBound = inputValueToWireBound(start, precision);
  const endBound = inputValueToWireBound(end, precision);
  const canApply = isValidCustomRange(startBound, endBound);

  return (
    <Popover.Root
      open={open}
      onOpenChange={(next: boolean) => {
        if (next) resetDraft();
        setOpen(next);
      }}
    >
      <Popover.Trigger
        render={
          <Button variant={active ? "solid" : "outline"} tone="neutral" size="sm">
            Custom
          </Button>
        }
      />
      <Popover.Content>
        <div className="bi-time-range-popover">
          {/* The calendar is the primary way to pick; the two inputs below it
           *  stay because they are what carries a TYPED date and the
           *  time-of-day half, and they remain the plainest keyboard path.
           *  The two drive one draft: picking a day rewrites only the date
           *  portion of each input (`withDatePart`), so a clock reading the
           *  reader already typed survives a change of day. */}
          <RangeCalendar
            start={start}
            end={end}
            onSelect={(from: CalDate, to: CalDate) => {
              setStart((v) => withDatePart(v, from));
              setEnd((v) => withDatePart(v, to));
            }}
          />
          <label className="bi-time-range-popover__toggle">
            <input
              type="checkbox"
              checked={precision === "datetime"}
              onChange={(e) => {
                const next: BoundPrecision = e.target.checked ? "datetime" : "date";
                setStart((v) => convertInputPrecision(v, next));
                setEnd((v) => convertInputPrecision(v, next));
                setPrecision(next);
              }}
            />
            Add a specific time (UTC)
          </label>
          <div className="bi-time-range-popover__row">
            <span className="bi-time-range-popover__row-label">From</span>
            <Input
              inputSize="sm"
              type={precision === "date" ? "date" : "datetime-local"}
              value={start}
              onChange={(e) => setStart(e.target.value)}
            />
          </div>
          <div className="bi-time-range-popover__row">
            <span className="bi-time-range-popover__row-label">To</span>
            <Input
              inputSize="sm"
              type={precision === "date" ? "date" : "datetime-local"}
              value={end}
              onChange={(e) => setEnd(e.target.value)}
            />
          </div>
          <div className="bi-time-range-popover__actions">
            <Button
              variant="soft"
              tone="neutral"
              size="sm"
              onClick={() => {
                onChange(setTimeRange(state, null, null));
                setOpen(false);
              }}
            >
              Clear
            </Button>
            <Button
              variant="solid"
              tone="accent"
              size="sm"
              disabled={!canApply}
              onClick={() => {
                if (!canApply) return;
                onChange(setTimeRange(state, startBound, endBound));
                setOpen(false);
              }}
            >
              Apply
            </Button>
          </div>
        </div>
      </Popover.Content>
    </Popover.Root>
  );
}

function SelectFilterControl({ filter, state, onChange, inactive }: FilterControlProps) {
  const existing = getFilterValue(state, filter.target_column ?? "");
  const [draft, setDraft] = useState(() =>
    Array.isArray(existing?.value) ? (existing.value as string[]).join(", ") : "",
  );
  const apply = () => {
    const selected = draft
      .split(",")
      .map((s) => s.trim())
      .filter((s) => s.length > 0);
    const value = filterValueForSelection(filter, selected);
    onChange(
      value
        ? setFilterValue(state, value.column, value.op, value.value)
        : filter.target_column
          ? {
              ...state,
              values: (state.values ?? []).filter((v) => v.column !== filter.target_column),
            }
          : state,
    );
  };
  return (
    <div
      className={inactive ? "bi-filter bi-filter--inactive" : "bi-filter"}
      title={inactive ? "Applies to no chart on this dashboard" : undefined}
    >
      <span className="bi-filter__label">{filter.name}</span>
      <Input
        inputSize="sm"
        placeholder={filter.multi_select ? "value1, value2, …" : "value"}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={apply}
        onKeyDown={(e) => {
          if (e.key === "Enter") apply();
        }}
      />
    </div>
  );
}

/** A `range` filter: two numeric bounds over a PAIR of the target dataset's
 *  declared parameters (see `rangeBounds`). The boxes open on the dataset's
 *  own defaults and apply on Enter or blur; a bound is only sent once it
 *  differs from the default, so an untouched control leaves the URL and the
 *  request exactly as they were. "Reset" returns both bounds to the defaults
 *  by REMOVING the overrides, not by writing the default values back — the
 *  dataset stays the one place the default lives. */
function RangeFilterControl({ filter, datasets, state, onChange, inactive }: FilterControlProps) {
  const bounds = rangeBounds(filter, datasets, state);
  const [lowDraft, setLowDraft] = useState(() => String(bounds?.low ?? ""));
  const [highDraft, setHighDraft] = useState(() => String(bounds?.high ?? ""));
  const appliedLow = bounds?.low;
  const appliedHigh = bounds?.high;
  // URL restoration happens after the controls mount. Synchronize applied
  // bounds without replacing a draft on unrelated filter edits or keystrokes.
  useEffect(() => {
    setLowDraft(String(appliedLow ?? ""));
    setHighDraft(String(appliedHigh ?? ""));
  }, [appliedLow, appliedHigh]);
  if (!bounds) return null;
  const lowName = filter.target_parameter_low ?? "";
  const highName = filter.target_parameter_high ?? "";

  const apply = () => {
    const low = lowDraft.trim() === "" ? null : Number(lowDraft);
    const high = highDraft.trim() === "" ? null : Number(highDraft);
    // Both boxes must hold a real number, and the pair must be a range: an
    // inverted pair would compile to `BETWEEN 5000 AND 500`, which BigQuery
    // answers with nothing, and an empty dashboard is the one answer a
    // reader cannot tell apart from "no such projects".
    if (
      (low !== null && (!Number.isFinite(low) || low < 0)) ||
      (high !== null && (!Number.isFinite(high) || high < 0)) ||
      (low !== null && high !== null && low > high)
    ) {
      setLowDraft(String(bounds.low ?? ""));
      setHighDraft(String(bounds.high ?? ""));
      return;
    }
    const overrides: Record<string, number | null> = {};
    const clear: string[] = [];
    if (low === bounds.defaultLow) clear.push(lowName);
    else overrides[lowName] = low;
    if (high === bounds.defaultHigh) clear.push(highName);
    else overrides[highName] = high;
    onChange(setParameterOverrides(clearParameterOverrides(state, clear), overrides));
  };

  const reset = () => {
    setLowDraft(String(bounds.defaultLow ?? ""));
    setHighDraft(String(bounds.defaultHigh ?? ""));
    onChange(clearParameterOverrides(state, [lowName, highName]));
  };

  const onKeyDown = (e: { key: string }) => {
    if (e.key === "Enter") apply();
  };

  return (
    <div
      className={inactive ? "bi-filter bi-filter--inactive" : "bi-filter"}
      title={inactive ? "Applies to no chart on this dashboard" : undefined}
    >
      <span className="bi-filter__label">{filter.name}</span>
      <div className="bi-filter__range" role="group" aria-label={filter.name}>
        <Input
          inputSize="sm"
          type="number"
          inputMode="decimal"
          aria-label={`${filter.name} minimum`}
          placeholder="No min"
          value={lowDraft}
          onChange={(e) => setLowDraft(e.target.value)}
          onBlur={apply}
          onKeyDown={onKeyDown}
        />
        <span className="bi-filter__range-to">to</span>
        <Input
          inputSize="sm"
          type="number"
          inputMode="decimal"
          aria-label={`${filter.name} maximum`}
          placeholder="No max"
          value={highDraft}
          onChange={(e) => setHighDraft(e.target.value)}
          onBlur={apply}
          onKeyDown={onKeyDown}
        />
        {bounds.overridden ? (
          <Button variant="soft" tone="neutral" size="sm" onClick={reset}>
            Reset
          </Button>
        ) : null}
      </div>
    </div>
  );
}
